import { Modal, notification } from 'antd';
import { useEffect, useState } from 'react';
import { Card, Empty } from 'antd';
import type {
	ColumnsType,
	TableRowSelection,
} from 'antd/es/table/interface';
import {
	useGetMaterialRequestQuery,
} from '../../../redux/api/material-request';
import { MaterialTypeObject } from '../../../interfaces/Material.Interface';

import BuildingTag from '../../BuildingTag';
import CustomResponsiveTable from '../../CustomResponsiveTable/index.tsx';

interface Props {
	visible: boolean;
	onAddItem: (requests: MaterialTypeObject[]) => void;
	material_request_ids: string[];
	material_vendor_id: string;
	building_id?: string | null;
	building_name?: string | null;
	onCancel: (value: boolean) => void;
}

const AddFromMaterialRequestModal = (props: Props) => {
	const [currentPage, setCurrentPage] = useState<number>(1);
	const selectedBuildingId = props.building_id ?? '';

	const { data, isLoading } = useGetMaterialRequestQuery({
		vendor: props.material_vendor_id,
		building: selectedBuildingId,
		status: 'Submitted',
		page: String(currentPage),
	}, {
		skip: !props.visible || !props.material_vendor_id || !selectedBuildingId,
	});

	const [selectedRowObjects, setSelectedRowObjects] = useState<
		MaterialTypeObject[]
	>([]);

	useEffect(() => {
		setCurrentPage(1);
		setSelectedRowObjects([]);
	}, [props.building_id, props.material_vendor_id, props.visible]);

	const isIdInMaterialRequestIds = (id: string) => {
		return props.material_request_ids.includes(id);
	};

	const rowSelection: TableRowSelection<MaterialTypeObject> = {
		onChange: (_selectedKeys, selectedRows) => {
			setSelectedRowObjects(selectedRows);
		},
		getCheckboxProps: (record) => ({
			disabled: isIdInMaterialRequestIds(record.id),
		}),
	};

	const handleAddSelected = () => {
		if (selectedRowObjects.length > 0) {
			props.onAddItem(selectedRowObjects);
			props.onCancel(false);
		} else {
			notification.error({
				message: 'No material selected',
				description: 'Please select a material to add',
			});
		}
	};

	const columns: ColumnsType<MaterialTypeObject> = [
		{
			title: 'Vendor',
			key: 'vendor',
			render: (record: MaterialTypeObject) => {
				return record.vendor_material?.vendor?.display_name;
			},
		},
		{
			title: 'Related Sales Order Line Item',
			render: (record: MaterialTypeObject) => {
				return record.order_line_item?.name;
			},
		},
		{
			title: 'Material Type',
			render: (record: MaterialTypeObject) => {
				return record.vendor_material?.type?.display_name;
			},
		},
		{
			title: 'Material Shape',
			render: (record: MaterialTypeObject) => {
				return record.vendor_material?.shape?.display_name;
			},
		},
		{
			title: 'Material Size',
			dataIndex: 'display_size',
		},
		{
			title: 'Length',
			dataIndex: 'total_required_length',
		},
		{
			title: 'Status',
			dataIndex: 'status',
		},
	];

	const emptyTable = () => {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={
					<span style={{ color: '#000' }}>
						{props.building_id
							? 'There are currently no materials to be displayed.'
							: 'Select a building in Order Details to view material requests.'}
					</span>
				}
			/>
		);
	};

	return (
		<>
			<Modal
				title={
					<span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
						Add From Material Request
						<BuildingTag name={props.building_name} showIcon={false} />
					</span>
				}
				open={props.visible}
				onOk={handleAddSelected}
				onCancel={() => props.onCancel(false)}
				width={1400}
				centered
				okText="Add Selected"
			>
				<Card className="shadow-sm" style={{ padding: 0 }}>
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<CustomResponsiveTable
							rowSelection={rowSelection}
							columns={columns}
							data={data?.results || []}
							scroll={{ y: `calc(95vh - 250px)` }}
							loading={isLoading}
							pagination={{
								pageSize: 20,
								current: currentPage,
								pageSizeOptions: [],
								total: data?.count || 0,
								onChange: async (page) => {
									setCurrentPage(page);
								},
								defaultPageSize: 20,
							}}
							emptyTable={emptyTable}
						/>
					</div>
				</Card>
			</Modal>
		</>
	);
};

export default AddFromMaterialRequestModal;
